<template>
  <div class="row">
    <div class="col-12 mb-3">
      <h1>{{ $t('merge.mergeProjects') }}</h1>
      <div class="lighter mb-2 w-50">{{ $t('merge.mergeProjectsDescription') }}</div>

      <organization-switcher
          :pre-set-organization-id="organizationId ? organizationId : null"
          :show-all-orgs="true"
          :show-teams="false"
          class="mb-2"
          @setOrganization="setOrganizationId"
      />
    </div>

    <div v-if="organizationId" class="col-12 col-md-6">
      <div class="p-2 bg-darkerdarker">
        <h3>{{ $t('merge.Project1') }}</h3>
        <div class="lighter mb-3">{{ $t('merge.mainProjectDescription') }}</div>
        <div v-if="!merged" class="vform-label white">{{ $t('merge.selectProject') }}</div>
        <universal-selector
            v-if="!merged"
            :limit-by-organization-id="organizationId"
            list-name="ProjectMergeList"
            store-name="Project"
            @addItem="(item) => { projectId1 = item.id; }"
        />
        <hr v-if="!merged" class="separation" />
        <project-merge-item
            v-if="projectId1"
            ref="project1"
            :project-id="projectId1"
            class="mb-2 project-item pr-2 pl-2 pb-2"
            @instancesErrored="project1ErroredInstances = true"
            @instancesFine="project1ErroredInstances = false"
            @permission="(val) => { projectEditPermission1 = val; }"
            @versionSet="(val) => { projectVersion1 = val; }"
        />
        <div v-else class="lighter mt-2">{{ $t('merge.noProjectSelected') }}</div>
      </div>
    </div>

    <div v-if="organizationId" class="col-12 col-md-6">
      <div class="p-2 bg-darkerdarker">
        <h3>{{ $t('merge.Project2') }}</h3>
        <div class="lighter mb-3 invisible">{{ $t('merge.mainProjectDescription') }}<!--Placeholder for spacing--></div>

        <div v-if="!merged" class="vform-label white mt-3">{{ $t('merge.selectProject') }}</div>
        <universal-selector
            v-if="!merged"
            :exclude-from-item-list="projectId1 ? [{id: projectId1}] : []"
            :limit-by-organization-id="organizationId"
            list-name="ProjectMergeList"
            store-name="Project"
            @addItem="(item) => { projectId2 = item.id; }"
        />
        <hr v-if="!merged" class="separation" />
        <project-merge-item
            v-if="projectId2"
            ref="project2"
            :project-id="projectId2"
            class="mb-2 project-item pr-2 pl-2 pb-2"
            @instancesErrored="project2ErroredInstances = true"
            @instancesFine="project2ErroredInstances = false"
            @permission="(val) => { projectEditPermission2 = val; }"
            @versionSet="(val) => { projectVersion2 = val; }"
        />
        <div v-else class="lighter mt-2">{{ $t('merge.noProjectSelected') }}</div>
      </div>
    </div>

    <div class="col-12">
      <div v-if="versionMismatch" class="form-error p-2 mt-2">
        <h2>{{ $t('merge.versionMismatch') }}</h2>
        <p>{{ $t('merge.versionMismatchSolution') }}</p>
      </div>

      <div v-if="projectId3" class="result-box">
        <h2 class="mt-2">{{ $t('merge.resultProject') }}</h2>
        <project-merge-item
            :project-id="projectId3"
            :show-button="true"
            class="mt-2 project-item p-2 hightlighted"
        />
      </div>

      <div v-if="!merged && projectId1 && projectId2 && !versionMismatch" class="bg-darkerdarker p-3 mt-3">
        <h3>{{ $t('merge.newProject') }}</h3>
        <div class="vform-label white mt-3">{{ $t('merge.projectName') }}</div>
        <input
            v-model="projectName"
            class="form-text form-text-dark mb-2"
            type="text"
            @input="delayTouch($v.projectName)"
        />
        <div v-if="$v.projectName.$dirty && !$v.projectName.required && $v.projectName.$error" class="form-error">
          {{ $t('errors.required') }}
        </div>
        <div v-if="$v.projectName.$dirty && !$v.projectName.alphaNumSpace" class="form-error">
          {{ $t('errors.alphaNumSpaceOnly') }}
        </div>

        <div v-if="$v.projectName.$dirty && !$v.projectName.maxLength" class="form-error">
          {{ $t('errors.atMostCharacters', {num: 128}) }}
        </div>

        <div v-if="$v.projectName.$dirty && !$v.projectName.minLength" class="form-error">
          {{ $t('errors.atLeastCharacters', {num: 3}) }}
        </div>

        <div class="vform-label white mt-3">{{ $t('teams') }}</div>
        <universal-selector
            :after-query-filter-async="checkTeamsForEditPermission"
            :limit-by-organization-id="organizationId"
            class="mt-2 mb-2"
            list-name="TeamMergeList"
            store-name="Team"
            @addItem="(item) => {
              teams.push(item)
            }"
        />
        <tag v-for="team in teams"
             :key="team.id"
             :is-removable="true"
             :name-property="'name'"
             :tag="team"
             class="mb-1"
             @removeTag="() => {
           teams = teams.filter(t => t.id !== team.id)
         }"
        />
      </div>

      <loading-spinner v-if="merging" class="mt-2 mb-2"/>
      <div v-if="msg" class="form-error p-2 mt-2">
        {{ $t(msg) }}
      </div>
      <div
          v-if="!merged"
          :class="['settings-button d-inline-block mt-2 action-button float-none', { 'disabled': !canStart }]"
          @click="canStart ? mergeProjects() : () => {}">{{ $t('merge.start') }}
      </div>
      <div
          v-else
          class="settings-button d-inline-block mt-2 action-button float-none"
          @click="resetParams">{{ $t('merge.reStart') }}
      </div>
    </div>

  </div>
</template>

<script>
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import ProjectMergeItem from "@/components/ProjectMergeItem.vue";
import UniversalSelector from "@/components/organizations/UniversalSelector.vue";
import OrganizationSwitcher from "@/components/organizations/OrganizationSwitcher.vue";
import Tag from "@/components/Tag.vue";
import {alphaNumSpace} from "@/customValidators";
import {required, minLength, maxLength} from 'vuelidate/lib/validators';
import VuelidateMixin from "@/components/mixins/VuelidateMixin.js.vue";

export default {
  name: "ProjectMerge",
  components: {
    LoadingSpinner,
    ProjectMergeItem,
    UniversalSelector,
    OrganizationSwitcher,
    Tag,
  },
  mixins: [VuelidateMixin],
  data() {
    return {
      projectId1: null,
      projectId2: null,
      projectId3: '',
      merging: false,
      msg: '',
      projectVersion1: 'unknown',
      projectVersion2: 'unknown',
      projectEditPermission1: true,
      projectEditPermission2: true,
      project1ErroredInstances: false,
      project2ErroredInstances: false,
      minimalProjectVersion: '3.1.3',
      organizationId: null,
      projectName: '',
      teams: [],
      merged: false,
    };
  },
  computed: {
    hasInstanceErrors() {
      // Check if either project has errored instances
      return this.project1ErroredInstances || this.project2ErroredInstances;
    },
    canStart() {
      // Check if both project IDs are set and versions are valid
      return this.projectId1 && this.projectId2 && !this.versionMismatch && this.projectName.trim() !== '' && !this.merging && !this.project2ErroredInstances && !this.project1ErroredInstances && this.projectEditPermission1 && this.projectEditPermission2;
    },
    versionMismatch() {
      if (this.projectId1 && this.projectId2) {
        // Compare the versions of the two projects
        return this.projectVersion1 !== this.projectVersion2;
      }
      return false;
    },
  },
  validations: {
    projectName: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(127),
      alphaNumSpace,
    },
  },
  watch: {
    hasInstanceErrors(newVal) {
      if (newVal) {
        this.msg = 'merge.instanceErrors';
      } else {
        this.msg = '';
      }
    },
  },
  mounted() {
    // Initialize organizationId if needed
    if (this.$route.query.organizationId) {
      this.organizationId = this.$route.query.organizationId;
    }
    if (this.$route.query.projectId1) {
      this.projectId1 = this.$route.query.projectId1;
    }
    if (this.$route.query.projectId2) {
      this.projectId2 = this.$route.query.projectId2;
    }
  },
  methods: {
    async checkTeamsForEditPermission(teams) {
      const filteredTeams = [];
      console.log('filtering teams', teams)
      for (let i = 0; i < teams.length; i++) {
        const team = teams[i];
        const hasAccess = await this.checkTeamAccess(team);
        console.log('hasAccess', hasAccess);
        if (hasAccess) {
          filteredTeams.push(team);
        }
      }
      console.log('filteredTeams', filteredTeams);
      return filteredTeams;
    },
    async checkTeamAccess(team) {
      return await this.$store.dispatch('checkTeamAccess', {
        op: 'create',
        teams: [team],
        opOnly: false
      });
    },
    setOrganizationId(organizationId) {
      if (this.organizationId === organizationId) {
        return; // No change in organization
      }
      this.organizationId = organizationId;
      if (!this.organizationId) {
        return;
      }
      this.resetParams();
    },
    resetParams() {
      this.projectId1 = null;
      this.projectId2 = null;
      this.projectId3 = '';
      this.projectName = '';
      this.teams = [];
      this.msg = '';
      this.projectVersion1 = 'unknown';
      this.projectVersion2 = 'unknown';
      this.projectEditPermission1 = true;
      this.projectEditPermission2 = true;
      this.project1ErroredInstances = false;
      this.project2ErroredInstances = false;
      this.merging = false;
      this.merged = false;
    },
    async mergeProjects() {
      this.msg = '';
      if (!this.projectId1 || !this.projectId2) {
        this.msg = 'merge.projectIdRequired';
        return;
      } else {
        this.merging = true;
        await this.$store.dispatch('clientMergeProjects', {
          id: this.projectId1,
          cid: this.projectId2,
          args: {
            name: this.projectName,
            teams: this.teams
          }
        }).then(project => {
          this.projectId3 = project.id;
          this.msg = 'merge.projectsMerged';
          this.merging = false;
          this.merged = true;
        }).catch(error => {
          console.error("Error merging projects:", error);
          console.log(error.statusCode)
          if (error.statusCode == "401") {
            this.msg = 'merge.accessMissing';
          } else {
            this.msg = 'merge.mergeFailed';
          }
          this.merging = false;
        });
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.project-item {
  background-color: var(--vform-editor-ui-secondary-color);
  color: white;

  &.hightlighted {
    background-color: $highlight;
  }
}

.form-error {
  max-width: 600px;
}

.bg-darkerdarker {
  background-color: var(--vform-editor-ui-secondary-color);
}
hr.separation {
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  height: 0;
  padding: 0;
  margin: 18px auto;
}
</style>